import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { AoSGameState, BuildTrackHistoryEntry } from '../../types';

/**
 * 線路建設をパス（手番終了）するMove
 *
 * プレイヤーが線路建設を終了する場合に呼び出されます。
 * タイルを配置せずにパスすることも可能です。
 *
 * コスト支払いはこのMove内で一括で行われます。
 * ※Detroit固有のエンジニアボーナスは passBuildTrackDetroit で実装
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @returns void or INVALID_MOVE
 */
export function passBuildTrack(
  { G, ctx }: { G: AoSGameState; ctx: Ctx }
): void | typeof INVALID_MOVE {
  const player = G.players[ctx.currentPlayer];

  // コスト集計
  const { totalCost, minCost } = calculateBuildCosts(player.buildTrackHistory || []);

  // エンジニアボーナス: MapConfig で有効かつ ENGINEER 選択時、最安タイルを無料
  let discount = 0;
  if (G.mapConfig?.hasEngineerBonus && player.selectedAction === 'ENGINEER') {
    discount = minCost;
  }

  const finalCost = totalCost - discount;

  // 支払い可能かチェック
  if (player.cash < finalCost) {
    console.error(`[passBuildTrack] Insufficient funds: need $${finalCost}, have $${player.cash}`);
    return INVALID_MOVE;
  }

  // コスト一括払い
  player.cash -= finalCost;

  // パスフラグを設定
  player.hasPassedBuild = true;

  debug.log(`[passBuildTrack] Player ${ctx.currentPlayer} paid $${finalCost} (total: $${totalCost}, discount: $${discount}), remaining cash: $${player.cash}`);
}

/**
 * 建設コスト集計ヘルパー
 *
 * @param history - 線路建設履歴
 * @returns 合計コストと最低コスト（新都市タイル$0は除外）
 */
export function calculateBuildCosts(history: BuildTrackHistoryEntry[]): { totalCost: number; minCost: number } {
  // 新都市タイル（$0）とコスト0のエントリを除外
  const costs = history
    .filter(e => !e.isNewCityTile && e.cost > 0)
    .map(e => e.cost);

  if (costs.length === 0) {
    return { totalCost: 0, minCost: 0 };
  }

  return {
    totalCost: costs.reduce((sum, c) => sum + c, 0),
    minCost: Math.min(...costs)
  };
}

/**
 * 建設コスト情報を更新（G.buildCostInfo）
 *
 * MapConfig.hasEngineerBonus に基づき、エンジニアボーナスを適用
 * この関数は buildTrack, buildSpecialLink, cancelAllBuildTrack の後で呼び出す
 *
 * @param G - ゲーム状態
 * @param playerId - プレイヤーID
 */
export function updateBuildCostInfo(G: AoSGameState, playerId: string): void {
  const player = G.players[playerId];
  const { totalCost, minCost } = calculateBuildCosts(player.buildTrackHistory || []);

  // エンジニアボーナス: MapConfig で有効かつ ENGINEER 選択時、最安タイルを無料
  let discount = 0;
  if (G.mapConfig?.hasEngineerBonus && player.selectedAction === 'ENGINEER') {
    discount = minCost;
  }

  const finalCost = totalCost - discount;

  G.buildCostInfo = {
    totalCost,
    discount,
    finalCost,
    isOverBudget: finalCost > player.cash,
  };
}
